// src/pages/admin/AdminDashboard.tsx
// Route: /admin/dashboard
// Fetches all data live from GET /api/admin/dashboard

import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectAdminUser,
  selectIsSuperAdmin,
} from "../../app/features/adminAuthSlice";
import { useAdminGetDashboardQuery } from "../../app/api/adminApi";
import { formatNaira } from "./AdminShared";

import { ArrowRight, Award, CreditCard, Hand, ShieldCheck, Sparkles, TrendingUp, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── STATUS BADGE ───────────────────────────────────────────────── */

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    confirmed: {
      label: "Confirmed",
      cls: "bg-[#edfaf4] text-[#2a7a56] border-[#b6e8d0]",
    },
    pending: {
      label: "Pending",
      cls: "bg-[#fff8ee] text-[#7a4a10] border-[#f5d9a0]",
    },
    withdrawn: {
      label: "Withdrawn",
      cls: "bg-[#fff3f3] text-[#9b2a2a] border-[#f5c0c0]",
    },
    completed: {
      label: "Completed",
      cls: "bg-[#ebeff7] text-[#4a44b0] border-[#cdd4e2]",
    },
  };
  const s = map[status] ?? map.pending;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[12.5px] font-semibold ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/* ─── PAY BADGE ──────────────────────────────────────────────────── */

function PayBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; dot: string; cls: string }> = {
    paid: {
      label: "Paid",
      dot: "bg-[#6cc2a0]",
      cls: "bg-[#edfaf4] text-[#2a7a56] border-[#b6e8d0]",
    },
    pending: {
      label: "Pending",
      dot: "bg-[#f0a050]",
      cls: "bg-[#fff8ee] text-[#7a4a10] border-[#f5d9a0]",
    },
    failed: {
      label: "Failed",
      dot: "bg-[#e57373]",
      cls: "bg-[#fff3f3] text-[#9b2a2a] border-[#f5c0c0]",
    },
  };
  const s = map[status] ?? map.pending;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${s.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/* ─── STAT CARD ──────────────────────────────────────────────────── */

function StatCard({
  label,
  value,
  delta,
  icon,
  accent,
  bg,
  loading,
}: {
  label: string;
  value: string;
  delta: string;
  icon: LucideIcon;
  accent: string;
  bg: string;
  loading: boolean;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-[24px] border border-[#e5e8ef] bg-white p-5 shadow-[0_4px_16px_rgba(27,45,94,0.05)]">
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#8189a0]">
          {label}
        </span>
        <span
          className="flex h-9 w-9 items-center justify-center rounded-[12px] text-base"
          style={{ background: bg, color: accent }}
        >
          <Ico icon={icon} />
        </span>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-8 w-24 animate-pulse rounded-xl bg-[#ebeff7]" />
          <div className="h-3 w-32 animate-pulse rounded-lg bg-[#f1f3f8]" />
        </div>
      ) : (
        <div>
          <p className="text-[2rem] font-bold leading-none tracking-[-0.04em] text-[#16213e]">
            {value}
          </p>
          <p className="mt-2 text-xs font-medium text-[#8189a0]">{delta}</p>
        </div>
      )}
    </div>
  );
}

/* ─── QUICK ACTIONS ──────────────────────────────────────────────── */

function QuickActions({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const actions = [
    {
      label: "Enrolments",
      to: "/admin/enrolments",
      icon: Sparkles,
      color: "#1b2d5e",
    },
    { label: "Progress", to: "/admin/progress", icon: TrendingUp, color: "#6cc2a0" },
    { label: "Payments", to: "/admin/payments", icon: CreditCard, color: "#efb0a4" },
    { label: "Students", to: "/admin/students", icon: Users, color: "#f0a050" },
    ...(isSuperAdmin
      ? [{ label: "Team", to: "/admin/team", icon: ShieldCheck, color: "#4a44b0" }]
      : []),
  ];
  return (
    <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-5 shadow-[0_4px_16px_rgba(27,45,94,0.05)]">
      <p className="mb-4 text-sm font-bold text-[#16213e]">Quick actions</p>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
        {actions.map((a) => (
          <Link
            key={a.to}
            to={a.to}
            className="flex flex-col items-center gap-2.5 rounded-[18px] border border-[#ebeff7] bg-[#f9fafc] px-3 py-4 text-center transition hover:border-[#cdd4e2] hover:bg-[#ebeff7]"
          >
            <span
              className="flex h-10 w-10 items-center justify-center rounded-[14px] text-base text-white"
              style={{ background: a.color }}
            >
              <Ico icon={a.icon} />
            </span>
            <span className="text-[12.5px] font-semibold leading-4 text-[#3b4560]">
              {a.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ─── TRACK DISTRIBUTION ─────────────────────────────────────────── */

const TRACK_META = [
  { key: "foundation_builders", label: "Foundation (6–9)", color: "#efb0a4" },
  { key: "explorers", label: "Explorers (9–12)", color: "#1b2d5e" },
  { key: "innovators", label: "Innovators (13–18)", color: "#4a44b0" },
] as const;

function TrackDistribution({
  dist,
  total,
  loading,
}: {
  dist: Record<string, number>;
  total: number;
  loading: boolean;
}) {
  return (
    <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-5 shadow-[0_4px_16px_rgba(27,45,94,0.05)] lg:col-span-2">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm font-bold text-[#16213e]">
          Enrolment by programme track
        </p>
        {loading ? (
          <div className="h-4 w-16 animate-pulse rounded-lg bg-[#ebeff7]" />
        ) : (
          <span className="text-xs text-[#8189a0]">{total} total</span>
        )}
      </div>
      <div className="space-y-4">
        {TRACK_META.map((t) => {
          const count = dist?.[t.key] ?? 0
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={t.key}>
              <div className="mb-1.5 flex items-center justify-between text-xs">
                <span className="font-medium text-[#3b4560]">{t.label}</span>
                {loading ? (
                  <div className="h-3 w-12 animate-pulse rounded-lg bg-[#ebeff7]" />
                ) : (
                  <span className="font-semibold text-[#16213e]">
                    {count}{" "}
                    <span className="font-normal text-[#8189a0]">({pct}%)</span>
                  </span>
                )}
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-[#ebeff7]">
                {!loading && (
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: t.color }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex flex-wrap gap-3 border-t border-[#ebeff7] pt-4">
        {TRACK_META.map((t) => (
          <div key={t.key} className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: t.color }}
            />
            <span className="text-[12.5px] text-[#5f6780]">{t.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── RECENT ENROLMENTS TABLE ────────────────────────────────────── */

function RecentEnrolments({
  enrolments,
  loading,
}: {
  enrolments: any[];
  loading: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[#f7f8fb]">
            {[
              "Student",
              "Age",
              "Track",
              "Status",
              "Payment",
              "Fee",
              "Date",
            ].map((h) => (
              <th
                key={h}
                className="px-5 py-3 text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f7f8fb]">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                <td colSpan={7} className="px-5 py-3.5">
                  <div className="h-5 w-full animate-pulse rounded-lg bg-[#f1f3f8]" />
                </td>
              </tr>
            ))
          ) : enrolments.length === 0 ? (
            <tr>
              <td
                colSpan={7}
                className="px-5 py-8 text-center text-xs text-[#8189a0]"
              >
                No enrolments yet
              </td>
            </tr>
          ) : (
            enrolments.map((e) => {
              const child = e.child;
              const trackLabel: Record<string, string> = {
                foundation_builders: "Foundation (6–9)",
                explorers: "Explorers (9–12)",
                innovators: "Innovators (13–18)",
              };
              return (
                <tr key={e.id} className="transition-colors hover:bg-[#f9fafc]">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[8px] bg-[#ebeff7] text-[11.5px] font-bold text-[#1b2d5e]">
                        {child
                          ? `${child.firstName.charAt(0)}${child.lastName.charAt(0)}`
                          : "?"}
                      </span>
                      <span className="font-medium text-[#16213e]">
                        {child
                          ? `${child.firstName} ${child.lastName}`
                          : "Unknown"}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[#5a6278]">
                    {child?.age ?? "—"}
                  </td>
                  <td className="px-5 py-3.5 text-xs text-[#5a6278]">
                    {trackLabel[child?.track ?? ""] ?? "—"}
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge status={e.status} />
                  </td>
                  <td className="px-5 py-3.5">
                    <PayBadge status={e.paymentStatus} />
                  </td>
                  <td className="px-5 py-3.5 text-xs font-semibold text-[#16213e]">
                    {e.feeAmount ? formatNaira(e.feeAmount) : "—"}
                    {e.billingLabel && (
                      <span className="ml-1 text-[11.5px] font-normal text-[#8189a0]">
                        ({e.billingLabel})
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-[12.5px] text-[#8189a0]">
                    {e.createdAt
                      ? new Date(e.createdAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <div className="border-t border-[#ebeff7] px-5 py-3.5">
        <Link
          to="/admin/enrolments"
          className="text-xs font-semibold text-[#1b2d5e] transition hover:text-[#4a44b0]"
        >
          View all enrolments{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function AdminDashboard() {
  const user = useSelector(selectAdminUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);

  const { data, isLoading, isError, refetch } = useAdminGetDashboardQuery();
  const stats = data?.data?.stats;
  const dist = data?.data?.trackDistribution ?? {
    foundation_builders: 0,
    explorers: 0,
    innovators: 0,
    unassigned: 0,
  };
  const recentEnrolments = data?.data?.recentEnrolments ?? [];
  const totalStudents = stats?.totalStudents ?? 0;
  const totalTrack =
    dist.foundation_builders +
    dist.explorers +
    dist.innovators +
    dist.unassigned;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const roleLabel: Record<string, string> = {
    super_admin: "Super Admin",
    admin: "Admin",
    staff: "Staff",
  };

  const STAT_CARDS = [
    {
      label: "Total students",
      icon: Users,
      accent: "#1b2d5e",
      bg: "#ebeff7",
      value: isLoading ? "—" : String(stats?.totalStudents ?? 0),
      delta: "all registered students",
    },
    {
      label: "Active enrolments",
      icon: Sparkles,
      accent: "#efb0a4",
      bg: "#fff4f1",
      value: isLoading ? "—" : String(stats?.activeEnrolments ?? 0),
      delta: "confirmed this quarter",
    },
    {
      label: "Total revenue",
      icon: CreditCard,
      accent: "#6cc2a0",
      bg: "#edfaf4",
      value: isLoading ? "—" : (stats?.totalRevenueFormatted ?? "₦0"),
      delta: "total paid payments",
    },
    {
      label: "Tracks running",
      icon: Award,
      accent: "#f0a050",
      bg: "#fff8ee",
      value: isLoading ? "—" : "3",
      delta: "Foundation · Explorers · Innovators",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">
            {greeting()}, {user?.firstName} <Hand className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </h1>
          <p className="mt-0.5 text-sm text-[#5f6780]">
            Here's what's happening at Future Smart Lab today.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="rounded-full bg-[#ebeff7] px-3 py-1 text-xs font-semibold text-[#4a44b0]">
            {roleLabel[user?.role ?? ""] ?? user?.role}
          </span>
          <span className="text-xs text-[#a6adbf]">
            {new Date().toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          {isError && (
            <button
              onClick={refetch}
              className="rounded-xl border border-[#f5c0c0] bg-[#fff3f3] px-3 py-1.5 text-xs font-semibold text-[#9b2a2a] hover:bg-[#ffe8e8]"
            >
              Retry
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {STAT_CARDS.map((s) => (
          <StatCard key={s.label} {...s} loading={isLoading} />
        ))}
      </div>

      {/* Middle row: track distribution + quick actions */}
      <div className="grid gap-4 lg:grid-cols-3">
        <TrackDistribution
          dist={dist}
          total={totalStudents || totalTrack}
          loading={isLoading}
        />
        <QuickActions isSuperAdmin={isSuperAdmin} />
      </div>

      {/* Recent enrolments */}
      <div className="rounded-[24px] border border-[#e5e8ef] bg-white shadow-[0_4px_16px_rgba(27,45,94,0.05)] overflow-hidden">
        <div className="border-b border-[#ebeff7] px-5 py-4">
          <p className="text-sm font-bold text-[#16213e]">Recent enrolments</p>
          <p className="mt-0.5 text-xs text-[#8189a0]">
            Latest 10 enrolments across all tracks
          </p>
        </div>
        <RecentEnrolments enrolments={recentEnrolments} loading={isLoading} />
      </div>

      {/* Super admin banner */}
      {isSuperAdmin && (
        <div className="flex flex-col gap-3 rounded-[24px] border border-[#dde2ec] bg-[#16213e] p-5 text-white sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold">Team management</p>
            <p className="mt-0.5 text-xs text-white/60">
              You have full access to add, update, and remove team members as
              super-admin.
            </p>
          </div>
          <Link
            to="/admin/team"
            className="flex-shrink-0 rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_4px_16px_rgba(27,45,94,0.35)] transition hover:bg-[#24397a]"
          >
            Manage team{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}
